import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Subscriptions() {
  const { user } = useApp();
  const { t } = useI18n();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    amount: '',
    currency: 'PLN',
    frequency: 'monthly', // weekly, monthly, yearly
    client_name: '',
    client_email: '',
    start_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadSubscriptions();
  }, []);

  async function loadSubscriptions() {
    try {
      const saved = localStorage.getItem('subscriptions');
      if (saved) {
        setSubscriptions(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function saveSubscription() {
    if (!form.name || !form.amount) {
      alert('Заполните название и сумму');
      return;
    }
    
    const newSub = {
      id: Date.now(),
      ...form,
      amount: parseFloat(form.amount),
      status: 'active',
      created_at: new Date().toISOString(),
      next_payment: calculateNextPayment(form.start_date, form.frequency),
    };
    
    const updated = [...subscriptions, newSub];
    setSubscriptions(updated);
    localStorage.setItem('subscriptions', JSON.stringify(updated));
    setShowForm(false);
    setForm({
      name: '',
      amount: '',
      currency: 'PLN',
      frequency: 'monthly',
      client_name: '',
      client_email: '',
      start_date: new Date().toISOString().split('T')[0],
    });
  }

  function calculateNextPayment(startDate, frequency) {
    const start = new Date(startDate);
    const now = new Date();
    let next = new Date(start);
    
    while (next <= now) {
      if (frequency === 'weekly') next.setDate(next.getDate() + 7);
      else if (frequency === 'monthly') next.setMonth(next.getMonth() + 1);
      else if (frequency === 'yearly') next.setFullYear(next.getFullYear() + 1);
    }
    
    return next.toISOString().split('T')[0];
  }

  function toggleStatus(id) {
    const updated = subscriptions.map(s => 
      s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s
    );
    setSubscriptions(updated);
    localStorage.setItem('subscriptions', JSON.stringify(updated));
  }

  function deleteSubscription(id) {
    const updated = subscriptions.filter(s => s.id !== id);
    setSubscriptions(updated);
    localStorage.setItem('subscriptions', JSON.stringify(updated));
  }

  const frequencyLabels = {
    weekly: 'Еженедельно',
    monthly: 'Ежемесячно',
    yearly: 'Ежегодно',
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const totalMonthly = subscriptions
    .filter(s => s.status === 'active')
    .reduce((sum, s) => {
      let monthly = s.amount;
      if (s.frequency === 'weekly') monthly *= 4;
      if (s.frequency === 'yearly') monthly /= 12;
      return sum + monthly;
    }, 0);

  return (
    <div className="page">
      <BackButton />
      <h1>🔄 Подписки</h1>

      {/* Summary */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        textAlign: 'center',
      }}>
        <div style={{ opacity: 0.8, fontSize: 13 }}>Ежемесячный доход от подписок</div>
        <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totalMonthly.toFixed(2)} PLN</div>
        <div style={{ opacity: 0.8, fontSize: 12 }}>{subscriptions.filter(s => s.status === 'active').length} активных</div>
      </div>

      {/* Add Button */}
      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: 16 }}
          onClick={() => setShowForm(true)}
        >
          ➕ Создать подписку
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}>
          <h3 style={{ marginTop: 0 }}>Новая подписка</h3>
          
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Название *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Например: Абонемент Premium"
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Сумма *</label>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="100"
                style={{
                  width: '100%', padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              />
            </div>
            <div style={{ width: 100 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Валюта</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                style={{
                  width: '100%', padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              >
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Периодичность</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            >
              <option value="weekly">Еженедельно</option>
              <option value="monthly">Ежемесячно</option>
              <option value="yearly">Ежегодно</option>
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Клиент</label>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Имя клиента"
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveSubscription}>
              💾 Сохранить
            </button>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
          <p style={{ opacity: 0.7 }}>Нет подписок</p>
          <p style={{ fontSize: 12, opacity: 0.5 }}>Создайте подписку для регулярных платежей</p>
        </div>
      ) : (
        <div className="list">
          {subscriptions.map(s => (
            <div key={s.id} style={{
              background: s.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
              opacity: s.status === 'active' ? 1 : 0.6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{s.name}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {s.client_name && `${s.client_name} • `}
                    {frequencyLabels[s.frequency]}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', color: '#10b981' }}>
                    {s.amount} {s.currency}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>
                    След: {s.next_payment}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-small"
                  style={{ flex: 1, background: s.status === 'active' ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)' }}
                  onClick={() => toggleStatus(s.id)}
                >
                  {s.status === 'active' ? '⏸ Пауза' : '▶️ Активировать'}
                </button>
                <button
                  className="btn btn-small"
                  style={{ background: 'rgba(239,68,68,0.3)' }}
                  onClick={() => deleteSubscription(s.id)}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
